import {
  Book,
  BookOpen,
  Box,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  ClipboardList,
  Factory,
  Globe,
  Layers,
  LayoutDashboard,
  LogOut,
  Settings,
  Shield,
  Store,
  TrendingUp,
  Truck,
  User,
  UserCog,
  Users,
  Wallet,
  Warehouse
} from 'lucide-react';
import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import './Sidebar.css';

const NAV_ITEMS = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Cuadro de Mando' },
  { id: 'books', icon: Book, label: 'Libros' },
  { id: 'authors', icon: User, label: 'Autores' },
  { id: 'editorial', icon: BookOpen, label: 'Editorial' },
  { id: 'clients', icon: Globe, label: 'Clientes' },
  { id: 'sales-channels', icon: TrendingUp, label: 'Canales de Venta' },
  { id: 'distribution', icon: Truck, label: 'Distribución' },
  { id: 'suppliers', icon: Factory, label: 'Acreedores' },
  { id: 'purchases', icon: Store, label: 'Compras' },
  { id: 'warehouses', icon: Warehouse, label: 'Almacenes' },
  { id: 'stock-audit', icon: ClipboardList, label: 'Auditoría de Stock' },
  { id: 'finances', icon: Wallet, label: 'Finanzas' },
  { id: 'agents', icon: UserCog, label: 'Agentes' },
  { id: 'societies', icon: Building2, label: 'Sociedades' },
  { id: 'users', icon: Users, label: 'Usuarios' },
  { id: 'roles', icon: Shield, label: 'Roles' },
  { id: 'grupos', icon: Layers, label: 'Grupos' },
];

type NavItem = typeof NAV_ITEMS[number];

// Orden guardado por usuario; las vistas nuevas van al final y las que ya no existen se descartan.
const loadNavOrder = (userId?: string | number): NavItem[] => {
  let saved: string[] = [];
  try {
    saved = userId ? JSON.parse(localStorage.getItem(`sidebar-order-${userId}`) || '[]') : [];
  } catch {
    saved = [];
  }
  const known = saved
    .map(id => NAV_ITEMS.find(item => item.id === id))
    .filter((item): item is NavItem => !!item);
  return [...known, ...NAV_ITEMS.filter(item => !known.includes(item))];
};

interface SidebarItemProps {
  icon: any;
  label: string;
  active?: boolean;
  onClick: (e: React.MouseEvent) => void;
  isCollapsed?: boolean;
}

const SidebarItem = ({ icon: Icon, label, active, onClick, isCollapsed }: SidebarItemProps) => (
  <button
    onClick={onClick}
    className={`sidebar-item-btn ${active ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
    title={isCollapsed ? label : undefined}
  >
    {active && <div className="sidebar-active-pill" />}
    <Icon size={18} className="sidebar-item-icon" />
    {!isCollapsed && (
      <>
        <span className="sidebar-item-label">{label}</span>
        <div className="sidebar-item-arrow">
          <ChevronRight size={14} />
        </div>
      </>
    )}
  </button>
);

interface SidebarItemWithSubmenuProps {
  icon: any;
  label: string;
  active?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  isCollapsed?: boolean;
  children: React.ReactNode;
}

const SidebarItemWithSubmenu = ({
  icon: Icon,
  label,
  active,
  isOpen,
  onToggle,
  isCollapsed,
  children
}: SidebarItemWithSubmenuProps) => (
  <div className="sidebar-submenu-container" onClick={(e) => e.stopPropagation()}>
    <button
      onClick={onToggle}
      className={`sidebar-item-btn ${active ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
      title={isCollapsed ? label : undefined}
    >
      {active && <div className="sidebar-active-pill" />}
      <Icon size={18} className="sidebar-item-icon" />
      {!isCollapsed && (
        <>
          <span className="sidebar-item-label">{label}</span>
          <div className={`sidebar-submenu-toggle-arrow ${isOpen ? 'open' : ''}`}>
            <ChevronDown size={14} />
          </div>
        </>
      )}
    </button>
    {!isCollapsed && (
      <div className={`sidebar-submenu-content ${isOpen ? 'open' : ''}`}>
        {children}
      </div>
    )}
  </div>
);



interface SidebarProps {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
  activeTab: string;
  handleTabClick: (tab: string) => void;
  setIsAuthenticated: (value: boolean) => void;
  currentUser?: any;
  users?: any[];
  setProfileModalUser?: (user: any) => void;
}

export const Sidebar = ({
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  activeTab,
  handleTabClick,
  setIsAuthenticated,
  currentUser,
  users,
  setProfileModalUser,
}: SidebarProps) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const userId = currentUser?.id;
  const [navItems, setNavItems] = React.useState<NavItem[]>(() => loadNavOrder(userId));

  React.useEffect(() => {
    setNavItems(loadNavOrder(userId));
  }, [userId]);

  const handleNavReorder = (items: NavItem[]) => {
    setNavItems(items);
    if (!userId) return;
    try {
      localStorage.setItem(`sidebar-order-${userId}`, JSON.stringify(items.map(item => item.id)));
    } catch {
      // Sin almacenamiento disponible el orden solo dura la sesión.
    }
  };

  React.useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.closest('.user-info-btn') || target.closest('.user-context-menu'))) {
        return;
      }
      setIsUserMenuOpen(false);
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
      {/* Collapse Toggle Button */}
      <button
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        className="sidebar-toggle-btn"
      >
        {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      <div className={`sidebar-header ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-logo">
          <BookOpen size={24} />
        </div>
        {!isSidebarCollapsed && (
          <div className="sidebar-title-container">
            <h2>MARCIAL PONS</h2>
            <span>ALBATROSS v0.1</span>
          </div>
        )}
      </div>

      <ReactSortable
        tag="nav"
        className="sidebar-nav" 
        list={navItems} 
        setList={handleNavReorder} 
        animation={150}
        forceFallback
        // Sin margen, 1px de temblor al pulsar se toma como arrastre y se pierde el clic.
        fallbackTolerance={15}
        scrollSensitivity={50}
        scrollSpeed={10}
        onStart={() => document.body.classList.add('sidebar-dragging')}
        onEnd={() => document.body.classList.remove('sidebar-dragging')}>

          {navItems.map(item => (
            <SidebarItem key={item.id} icon={item.icon} label={item.label} active={activeTab === item.id} isCollapsed={isSidebarCollapsed} onClick={() => handleTabClick(item.id)} />
          ))}
      </ReactSortable>

      <div className="sidebar-footer">
        <div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsUserMenuOpen(!isUserMenuOpen);
            }}
            className={`user-info-btn ${isSidebarCollapsed ? 'collapsed' : ''}`}
          >
            <div className="user-avatar-container">
              {currentUser?.photoUrl ? (
                <img src={currentUser.photoUrl} alt={currentUser.name} referrerPolicy="no-referrer" className="user-avatar-img" />
              ) : (
                <User size={16} className="user-avatar-icon" />
              )}
            </div>
            {!isSidebarCollapsed && (
              <div className="user-details">
                <span className="user-name">
                  {currentUser?.name || 'M. PONS'}
                </span>
                <span className="user-role">
                  {currentUser?.role || 'LEVEL_5_ADMIN'}
                </span>
              </div>
            )}

            {/* Menú de Cuenta contextual */}
            {isUserMenuOpen && (
              <div
                className={`user-context-menu ${isSidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    if (setProfileModalUser) setProfileModalUser(currentUser || (users && users.length > 0 ? users[0] : null));
                    setIsUserMenuOpen(false);
                  }}
                  className="user-context-menu-btn"
                >
                  <User size={14} className="user-context-icon" />
                  Ver_Perfil
                </button>
                <button
                  onClick={() => {
                    handleTabClick('administration');
                    setIsUserMenuOpen(false);
                  }}
                  className="user-context-menu-btn"
                >
                  <Settings size={14} className="user-context-icon" />
                  Administracion
                </button>
                <div className="user-context-divider" />
                <button
                  onClick={() => setIsAuthenticated(false)}
                  className="user-context-menu-btn logout"
                >
                  <LogOut size={14} />
                  Cerrar_Sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    </aside>
  );
};
